import { useMemo } from 'react'

import type { UseFiltersPayload } from '@domain-group/react-hooks'
import { useNumericParams, useModals } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useSetState from '@hooks/useSetState'

import BulkCreateProductOptionProductsModal from '@modals/BulkCreateProductOptionProductsModal'
import BulkCreateProductOptionValuesSubsetsModal from '@modals/BulkCreateProductOptionValuesSubsetsModal'
import DuplicateProductOptionModal from '@modals/DuplicateProductOptionModal'

import {
  ProductOptionModel,
  ProductOptionProductModel,
  ProductOptionValuesSubsetModel,
} from '@models/types'

import PageContent from './components/PageContent'

export type PageContextPayload = UseFiltersPayload & {
  callbacks: {
    deleteProductOptionProduct: (payload: HandleCallbackActionParams) => Promise<object>,
    showBulkCreateProductOptionValuesSubsetsModal: (payload: { productOptionValuesSubset: ProductOptionValuesSubsetModel }) => void,
    showDuplicateProductOptionModal: (payload: { productOption: ProductOptionModel }) => void,
  },
}

const defaultState = {
  showBulkCreateProductOptionProductsModal: false,
  showBulkCreateProductOptionValuesSubsetsModal: false,
  showDuplicateProductOptionModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

/* eslint-disable no-undef */
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    BulkCreateProductOptionProductsModal: {
      bulkCreateProductOptionProducts: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      closeModal: () => setState({ showBulkCreateProductOptionProductsModal: false }),
    },
    BulkCreateProductOptionValuesSubsetsModal: {
      bulkCreateProductOptionValuesSubsets: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      closeModal: () => setState({ showBulkCreateProductOptionValuesSubsetsModal: false }),
    },
    DuplicateProductOptionModal: {
      closeModal: () => setState({ showDuplicateProductOptionModal: false }),
      duplicateProductOption: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const Relations = () => {
  const { productOptionId } = useNumericParams()

  const [state, setState] = useSetState(defaultState)
  const {
    showBulkCreateProductOptionProductsModal,
    showBulkCreateProductOptionValuesSubsetsModal,
    showDuplicateProductOptionModal,
  } = state

  const modalContext = useModals()
  const { callbacks: { setModalData } } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      deleteProductOptionProduct: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      duplicateProductOptionValue: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      showBulkCreateProductOptionProductsModal: (payload: { productOptionProduct: ProductOptionProductModel }) => {
        setModalData('BulkCreateProductOptionProductsModal', payload)
        setState({ showBulkCreateProductOptionProductsModal: true })
      },
      showBulkCreateProductOptionValuesSubsetsModal: (payload: {
        productOptionValuesSubset: ProductOptionValuesSubsetModel,
      }) => {
        setModalData('BulkCreateProductOptionValuesSubsetsModal', payload)
        setState({ showBulkCreateProductOptionValuesSubsetsModal: true })
      },
      showDuplicateProductOptionModal: (payload: { productOption: ProductOptionModel }) => {
        setModalData('DuplicateProductOptionModal', payload)
        setState({ showDuplicateProductOptionModal: true })
      },
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent productOptionId={productOptionId} />

        <BulkCreateProductOptionProductsModal
          callbacks={callbacks('BulkCreateProductOptionProductsModal', setState)}
          showModal={showBulkCreateProductOptionProductsModal}
        />

        <BulkCreateProductOptionValuesSubsetsModal
          callbacks={callbacks('BulkCreateProductOptionValuesSubsetsModal', setState)}
          showModal={showBulkCreateProductOptionValuesSubsetsModal}
        />

        <DuplicateProductOptionModal
          callbacks={callbacks('DuplicateProductOptionModal', setState)}
          showModal={showDuplicateProductOptionModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Relations
